import { ref, reactive, computed, onMounted, watch } from 'vue'
import { defineStore } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import API from '@util/API'
import defaultWidget from './defaultWidget'
import { useModal } from './modal'
import { useTranslation } from '../composables/useTranslation'

export const useWidgets = defineStore('widgets', () => {

    const { t } = useTranslation();

    // States.
    const states = reactive({
        errors: {},
        showSuccess: false,
        defaultCountryCode: 44
    })

    // Widget Tabs 
    const route = useRoute();
    const router = useRouter();

    const tabs = ref([
        {
            id: 1,
            path: 'whatsapp',
            title: t('tab_whatsapp'),
        },
        {
            id: 2,
            path: 'customize',
            title: t('tab_customize'),
        },
        {
            id: 3,
            path: 'greetings',
            title: t('tab_greetings'),
        },
        {
            id: 4,
            path: 'triggers',
            title: t('tab_triggers'),
        },
    ])

    const isValidPhone = (phone = '') => {
        return phone?.match(/^\d{7,15}$/)
    }

    const setError = (field, value = true) => {
        states.errors[field] = value

        setTimeout(() => {
            const target = document.querySelector(`[name="${field}"]`)
            if (target) {
                // Scroll to the error field.
                target.scrollIntoView({ behavior: 'smooth', block: 'center' })

                // Focus on the field.
                target.focus()
            }
        }, 0)

        return false
    }

    const setTab = async (tab) => {

        // Middleware.
        states.errors = {}
        const config = currentWidget.value.config

        if (isTab('whatsapp')) {

            if (config.whatsapp.agent_mode === 'single') {
                const hasValidPhone = isValidPhone(config.whatsapp.number || '');
                const groupCodeRaw = (config.whatsapp.group_invite_code || '').trim();
                const groupMatch = groupCodeRaw.match(/chat\.whatsapp\.com\/([A-Za-z0-9_-]+)/);
                const hasValidGroup = (groupMatch ? groupMatch[1] : groupCodeRaw).length > 0;

                if (!hasValidPhone && !hasValidGroup) {
                    if (config.whatsapp.destination_type === 'group') {
                        setError('group_invite_code')
                    } else {
                        setError('whatsapp_number')
                    }
                }
            } else {
                // Check if at least one agent is set.
                if (!config.whatsapp.agents || config.whatsapp.agents.length === 0) {
                    setError('whatsapp_agents')
                }
                // To each.
                config.whatsapp.agents.forEach((agent, index) => {
                    if (!isValidPhone(agent.number || '')) {
                        setError(`agent_number_${index}`)
                        return
                    }
                })
            }

        }

        if (isTab('customize')) {

            // cf7 form configuration 
            if (config.form.mode === 'cf7' && !config.form.cf7_id) {
                setError('cf7_id')
            }

            // Gravity
            if (config.form.mode === 'gravity' && !config.form.gravity_id) {
                setError('gravity_id')
            }
            // WPForms
            if (config.form.mode === 'wpforms' && !config.form.wpforms_id) {
                setError('wpforms_id')
            }

            // forminator
            if (config.form.mode === 'forminator' && !config.form.forminator_id) {
                setError('forminator_id')
            }

            // formidable
            if (config.form.mode === 'formidable' && !config.form.formidable_id) {
                setError('formidable_id')
            }

            // ninja
            if (config.form.mode === 'ninja' && !config.form.ninja_id) {
                setError('ninja_id')
            }

            // If emails are enabeld and no email is set.
            if (config.email.enabled && !config.email.admin_email && config.email.address == '') {
                setError('email_address')
            }
        }

        // Bail if errors.
        if (Object.keys(states.errors).length > 0) {
            return false
        }

        router.push({ name: 'widget.edit', params: { widgetId: route.params.widgetId }, query: { tab: tab } })

        // Scroll to top.
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 0)
    }

    const isTab = (tab) => {
        return route.query.tab == tab;
    }

    const currentTab = computed(() => tabs.value.find(tab => tab.path == route.query.tab) || tabs.value[0])
    const isTabEmpty = computed(() => !route.query.tab)

    const isTabDone = (_tab) => {
        const tabIndex = tabs.value.findIndex(tab => tab.path == _tab);
        const currentIndex = tabs.value.findIndex(tab => tab.path == route.query.tab);
        return tabIndex <= currentIndex;
    }
    const nextTab = () => {
        const currentIndex = tabs.value.findIndex(tab => tab.path == route.query.tab);
        if (currentIndex < tabs.value.length - 1) {
            // find tab name.
            setTab(tabs.value[currentIndex + 1].path)
        }
    }

    const prevTab = () => {
        const currentIndex = tabs.value.findIndex(tab => tab.path == route.query.tab);
        if (currentIndex > 0) {
            // find tab name.
            setTab(tabs.value[currentIndex - 1].path)
        }
    }

    // Widgets.
    const widgets = ref([])

    const clone = async (widget) => {

        // Clone triggers.
        const response = await API.post(`widget`, widget)

        if (response.data.success) {
            widgets.value.push(response.data.data)
            router.push({ name: 'widget.edit', params: { widgetId: response.data.data.id }, query: { tab: 'whatsapp' } })
        }
    }

    const editWidget = widget => {
        setCurrentWidget(widget)

        router.push({ name: 'widget.edit', params: { widgetId: widget.id || 'new' }, query: { tab: 'whatsapp' } })
    }

    const currentWidget = ref(defaultWidget)
    const setCurrentWidget = (widget) => {
        currentWidget.value = widget

        const DEFAULT = defaultWidget.config
        const config = { ...widget.config };

        if (!formychat_admin_vars.is_premium) {

            config.whatsapp.web_version = DEFAULT.whatsapp.web_version;
            config.whatsapp.agent_mode = DEFAULT.whatsapp.agent_mode;

            config.icon.image_url = DEFAULT.icon.image_url;
            config.icon.size = widget.config.icon.size === 'custom' ? DEFAULT.icon.size : widget.config.icon.size;
            config.icon.position = widget.config.icon.position === 'custom' ? DEFAULT.icon.position : widget.config.icon.position;

            config.cta.color = DEFAULT.cta.color;
            config.cta.background_color = DEFAULT.cta.background_color;

            config.form.size = widget.config.form.size === 'custom' ? DEFAULT.form.size : widget.config.form.size;
            config.form.show_country_code_field = DEFAULT.form.show_country_code_field;
            config.form.text_color = DEFAULT.form.text_color;
            config.form.background_color = DEFAULT.form.background_color;

            config.greetings.style = DEFAULT.greetings.style;
            config.greetings.on_click = DEFAULT.greetings.on_click;

            config.greetings.templates.simple.heading_size = widget.config.greetings.templates.simple.heading_size === 'custom' ? DEFAULT.greetings.templates.simple.heading_size : widget.config.greetings.templates.simple.heading_size;
            config.greetings.templates.simple.message_size = widget.config.greetings.templates.simple.message_size === 'custom' ? DEFAULT.greetings.templates.simple.message_size : widget.config.greetings.templates.simple.message_size;
            config.greetings.templates.simple.message_color = widget.config.greetings.templates.simple.message_color === 'custom' ? DEFAULT.greetings.templates.simple.message_color : widget.config.greetings.templates.simple.message_color;
            config.greetings.templates.simple.background_color = widget.config.greetings.templates.simple.background_color === 'custom' ? DEFAULT.greetings.templates.simple.background_color : widget.config.greetings.templates.simple.background_color;

            config.greetings.templates.wave.icon_url = DEFAULT.greetings.templates.wave.icon_url;
            config.greetings.templates.wave.icon_position = DEFAULT.greetings.templates.wave.icon_position;
            config.greetings.templates.wave.heading_size = widget.config.greetings.templates.wave.heading_size === 'custom' ? DEFAULT.greetings.templates.wave.heading_size : widget.config.greetings.templates.wave.heading_size;
            config.greetings.templates.wave.message_size = widget.config.greetings.templates.wave.message_size === 'custom' ? DEFAULT.greetings.templates.wave.message_size : widget.config.greetings.templates.wave.message_size;
            config.greetings.templates.wave.message_color = DEFAULT.greetings.templates.wave.message_color;
            config.greetings.templates.wave.background_color = DEFAULT.greetings.templates.wave.background_color;
            config.greetings.templates.wave.cta_icon_url = DEFAULT.greetings.templates.wave.cta_icon_url;
            config.greetings.templates.wave.cta_text_color = DEFAULT.greetings.templates.wave.cta_text_color;
            config.greetings.templates.wave.cta_background_color = DEFAULT.greetings.templates.wave.cta_background_color;
            config.greetings.templates.wave.cta_heading_color = DEFAULT.greetings.templates.wave.cta_heading_color;
            config.greetings.templates.wave.cta_message_color = DEFAULT.greetings.templates.wave.cta_message_color;
            config.greetings.templates.wave.cta_heading_size = widget.config.greetings.templates.wave.cta_heading_size === 'custom' ? DEFAULT.greetings.templates.wave.cta_heading_size : widget.config.greetings.templates.wave.cta_heading_size;
            config.greetings.templates.wave.cta_message_size = widget.config.greetings.templates.wave.cta_message_size === 'custom' ? DEFAULT.greetings.templates.wave.cta_message_size : widget.config.greetings.templates.wave.cta_message_size;

            // Adjust icon.
            config.icon = {
                ...formychat_admin_vars.data.widget_config.icon,
                ...config.icon,
                ...DEFAULT.icon,
                ...currentWidget.value.config.icon
            }

            // Turn off fluentcrm.
            try {
                config.integrations['fluent-crm'].enabled = false;
            } catch (e) {
                console.error('Error turning off fluentcrm', e)
            }
        }

        // if ( currentWidget.value.config?.icon ) {
        //     currentWidget.value.config.icon = {
        //         ...DEFAULT.icon,
        //         ...currentWidget.value.config.icon
        //     }
        // }

        if (config.whatsapp && !config.whatsapp.agent_mode) {
            config.whatsapp.agent_mode = 'single'
        }

        // Check for existing data first
        // let currentCountryCode = config?.whatsapp?.country_code || null
        // if ( !currentCountryCode && window.formychat_country_code) {
        //     console.log('get', config.whatsapp.country_code);
        //     config.whatsapp.country_code = window.formychat_country_code.toString() || '44';
        //     console.log('set', config.whatsapp.country_code);
        // }

        // Set.
        currentWidget.value = { ...widget, config }
    }

    const setCountryCode = (country_code) => {
        currentWidget.value.config.whatsapp.country_code = country_code
    }

    const deleteWidgets = async (ids = []) => {

        return new Promise(async (resolve) => {
            // Convert to array.
            if (!Array.isArray(ids)) {
                ids = [ids]
            }

            // If no ids provided, delete current widget.
            if (ids.length === 0) {
                ids = [currentWidget.value.id]
            }

            const selectedWidgets = widgets.value.filter(w => ids.includes(w.id))

            // Set loading.
            selectedWidgets.forEach(w => w.deleting = true)

            // Delete widget from server.
            const deleted = await API.delete(`widgets`, { data: { id: ids } })

            if (deleted.data.success) {
                selectedWidgets.forEach(w => w.deleted = true)
                setTimeout(() => {
                    // Splice from widgets.
                    widgets.value = widgets.value.filter(w => !ids.includes(w.id))
                    resolve(widgets.value)
                }, 500)
            } else {
                selectedWidgets.forEach(w => w.deleting = false)
                resolve(widgets.value)
            }

        })
    }

    const saveWidget = async (widget = null) => {
        widget = widget || currentWidget.value


        if (widget.is_saving) {
            return false
        }

        // Client-side validation: FluentCRM email mapping must exist when enabled
        const validateFluentCrm = () => {
            try {
                const settings = currentWidget.value?.config?.integrations?.['fluent-crm'] || {}
                if (!settings.enabled) return true

                const mapping = settings.fields_mapping || []

                // Accept either array of {from,to} or associative { email: 'field_key' }
                let emailFrom = ''
                if (Array.isArray(mapping)) {
                    for (const map of mapping) {
                        if (map && map.to === 'email' && map.from) {
                            emailFrom = map.from
                            break
                        }
                    }
                } else if (mapping && typeof mapping === 'object') {
                    emailFrom = mapping.email || ''
                }

                if (!emailFrom) {
                    setError('fluentcrm_email_mapping')
                    return false
                }

                // Optionally ensure the mapped field exists in current fields
                const availableFields = Object.keys(fields.value || {})
                if (availableFields.length && !availableFields.includes(emailFrom)) {
                    setError('fluentcrm_email_mapping')
                    return false
                }

                return true
            } catch (e) {
                setError('fluentcrm_email_mapping')
                return false
            }
        }

        if (!validateFluentCrm()) {
            return false
        }

        // Client-side validation: Mailchimp email mapping
        const validateMailchimp = () => {
            try {
                const settings = currentWidget.value?.config?.integrations?.['mailchimp'] || {}
                if (!settings.enabled) return true

                const mapping = settings.fields_mapping || []

                // Validate email mapping
                let emailFrom = ''
                if (Array.isArray(mapping)) {
                    for (const map of mapping) {
                        if (map && map.to === 'EMAIL' && map.from) {
                            emailFrom = map.from
                            break
                        }
                    }
                } else if (mapping && typeof mapping === 'object') {
                    emailFrom = mapping.EMAIL || ''
                }

                if (!emailFrom) {
                    setError('mailchimp_email_mapping')
                    return false
                }

                // Ensure the mapped field exists in current fields
                const availableFields = Object.keys(fields.value || {})
                if (availableFields.length && !availableFields.includes(emailFrom)) {
                    setError('mailchimp_email_mapping')
                    return false
                }

                return true
            } catch (error) {
                console.error('Mailchimp validation error:', error)
                setError('mailchimp_email_mapping')
                return false
            }
        }

        if (!validateMailchimp()) {
            return false
        }

        if (!widget.is_active) {
            let userConsent = await useModal().fire({
                title: t('widget_turned_off'),
                message: t('widget_turned_off_message'),
                ok: t('save_and_show'),
                cancel: t('save_keep_off'),
            })

            if (userConsent.ok) {
                widget.is_active = true
            }

            if (userConsent.cancel) {
                widget.is_active = false
            }

            if (userConsent.close) {
                return;
            }
        }

        widget.is_loading = true

        const isNew = widget.id === null || widget.id === undefined || widget.id === 0
        return new Promise(resolve => {

            widget.is_saving = true

            const payload = { ...widget }

            // Attach recaptcha / turnstile options when the built-in form is active.
            if (currentWidget.value?.config?.form?.mode === 'formychat') {
                if (recaptchaOptions.value) {
                    payload.recaptcha_options = { ...recaptchaOptions.value }
                }
                if (turnstileOptions.value) {
                    payload.turnstile_options = { ...turnstileOptions.value }
                }
            }

            const request = isNew ? API.post('widget', payload) : API.put(`widget/${widget.id}`, payload)

            request.then(response => {
                // console.log('Widget saved', response.data);

                if (isNew) {
                    currentWidget.value.id = response.data.id
                }

                states.showSuccess = true

                // Keep formychat_admin_vars in sync so Customize.vue re-reads correct values.
                if (response.data?.recaptcha) {
                    window.formychat_admin_vars = window.formychat_admin_vars || {}
                    window.formychat_admin_vars.recaptcha = response.data.recaptcha
                }
                if (response.data?.turnstile) {
                    window.formychat_admin_vars = window.formychat_admin_vars || {}
                    window.formychat_admin_vars.turnstile = response.data.turnstile
                }

                resolve(response.data)
            }).catch(error => {
                // console.error("Error saving widget", error)  
                resolve(null)
            }).finally(() => {
                widget.is_loading = false
                widget.is_saving = false
            })
        })
    }

    const fetchWidgets = async () => {
        states.loading = true
        return new Promise(resolve => {

            API.get('widgets').then(response => {
                widgets.value = response.data.map(w => {
                    return { ...w, is_loading: false, is_selected: false }
                })
                // console.log("Widgets fetched");
                resolve(widgets.value)
            }).catch(error => {
                // console.error("Error fetching widgets", error)  
                resolve(widgets.value)
            }).finally(() => {
                states.loading = false
            })
        })
    }

    // Filters.
    const filters = reactive({
        sortBy: 'created_at',
        sortOrder: 'desc'
    })

    watch([() => filters.sortBy, () => filters.sortOrder], () => {
        // Sort widgets.
        widgets.value.sort((a, b) => {
            if (filters.sortOrder === 'asc') {
                return a[filters.sortBy] > b[filters.sortBy] ? 1 : -1
            } else {
                return a[filters.sortBy] < b[filters.sortBy] ? 1 : -1
            }
        })

    }, { deep: true })

    const setSort = (sortBy) => {
        if (filters.sortBy === sortBy) {
            filters.sortOrder = filters.sortOrder === 'asc' ? 'desc' : 'asc'
        } else {
            filters.sortBy = sortBy
            filters.sortOrder = 'asc'
        }
    }

    const getWidgets = computed(() => {
        return widgets.value || []
    })

    // ── reCAPTCHA / Turnstile (global, per-save) ──────────────────────────────
    const recaptchaOptions = ref(null)   // { enabled, site_key, secret_key, version }
    const turnstileOptions = ref(null)   // { enabled, site_key, secret_key }

    const setRecaptchaOptions = (opts) => {
        recaptchaOptions.value = { ...opts }
    }

    const setTurnstileOptions = (opts) => {
        turnstileOptions.value = { ...opts }
    }

    const recaptchaSecretKeyConfigured = computed(
        () => !!(window.formychat_admin_vars?.recaptcha?.secret_key_configured)
    )

    const turnstileSecretKeyConfigured = computed(
        () => !!(window.formychat_admin_vars?.turnstile?.secret_key_configured)
    )

    /**
     * Mirrors the frontend helper — treats false / 0 / "0" / "false" as off.
     */
    const isSendMessageToWhatsappEnabled = (whatsappConfig) => {
        const v = whatsappConfig?.send_message_to_whatsapp
        if (v === false || v === 0 || v === '0' || v === 'false') return false
        return true
    }

    /**
     * Update widget.
     */
    const update = async (widget) => {
        const current = widgets.value.find(w => w.id === widget.id)
        current.statusLoading = true
        await API.put(`widget/${widget.id}`, widget)
        current.statusLoading = false
    }

    // screen 
    const isMobile = ref(window.innerWidth < 640)

    window.addEventListener('resize', () => {
        isMobile.value = window.innerWidth < 640
    })

    /**
     * Fetch Contents
     */
    const contents = ref({
        countries: [],
        fonts: [],
        pages: [],
        posts: [],
        forms: {}
    })
    const fetchContents = async () => {

        let data = (await API.get('contents')).data

        // Format countries.
        contents.value.countries = data.countries?.sort((a, b) => a.name.localeCompare(b.name)).map(country => {
            return {
                value: country.code,
                label: `<span>${country.flag} (${country.code})</span> ${country.name}`,
                name: `<span>${country.flag}</span> ${country.code}`
            }
        });

        // console.log('data.fonts', Object.keys(data.fonts), typeof data.fonts);

        // Format fonts.
        contents.value.fonts = Object.keys(data.fonts)?.map((font, index) => {
            return {
                value: font,
                name: data.fonts[font],
                label: `<span style="font-family: ${font}">${data.fonts[font]}</span> <ProBadge/>`,
                locked: index > 10 && !formychat_admin_vars.is_premium
            }
        });

        // Format pages.
        contents.value.pages = data.pages.map(page => {
            return {
                value: page.ID,
                name: page.post_title,
                label: page.post_title
            }
        });

        // Format posts.
        contents.value.posts = (data.posts || []).map(post => {
            return {
                value: post.ID,
                name: post.post_title,
                label: post.post_title
            }
        });

        // Forms.
        contents.value.forms = data.forms || {}
        // console.log('contents.value.forms', contents.value.forms);

    }

    const toTitleCase = (str) => {
        return str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    }

    /**
     * Fields.
     */
    const fields = ref([])
    const fetchFields = async () => {
        states.loadingFields = true;
        const result = await API.get('form_fields?mode=' + currentWidget.value.config.form.mode + '&form_id=' + currentWidget.value.config.form[currentWidget.value.config.form.mode + '_id'] || '');

        fields.value = result.data.data || {}

        if (currentWidget.value.config.form.mode != 'formychat' && !currentWidget.value.config.whatsapp[currentWidget.value.config.form.mode + '_message_template']) {
            let template = '';
            for (const field in fields.value) {
                template += `${toTitleCase(field)} : {${field}}\n`;
            }
            currentWidget.value.config.whatsapp[currentWidget.value.config.form.mode + '_message_template'] = template;
        }
        states.loadingFields = false;
    }

    onMounted(fetchContents)

    return {
        tabs,
        currentTab,
        setTab,
        isTab,
        prevTab,
        nextTab,
        isTabEmpty,
        isTabDone,
        states,
        widgets,
        filters,
        getWidgets,
        setCurrentWidget,
        clone,
        editWidget,
        deleteWidgets,
        fetchWidgets,
        saveWidget,
        setSort,
        isLoading: computed(() => states.loading),
        totalWidgets: computed(() => widgets.value.length),
        update,
        isMobile,
        currentWidget,
        setError,
        errors: computed(() => states.errors),
        contents,
        fetchContents,
        setCountryCode,
        isValidPhone,
        fields,
        fetchFields,
        setRecaptchaOptions,
        setTurnstileOptions,
        recaptchaOptions,
        turnstileOptions,
        recaptchaSecretKeyConfigured,
        turnstileSecretKeyConfigured,
        isSendMessageToWhatsappEnabled,
    }
})